import { useMutation } from "@apollo/react-hooks";
import { Button, Modal, TextField } from "@shopify/polaris";
import React, { useCallback, useReducer, useState } from "react";
import { CREATE_AMAZON_IDEA_TEMPLATE } from "../../graphql/mutations/amazon-idea";
import { arrInvalid, handleError, reducerFn } from "../../helper";
import useTimeout from "../../hooks/useTimeout";
import { ComponentLabelPolaris } from "../shared/ComponentLabelPolaris";
import { useToastContext } from "../shared/ToastContext";

const initialState = {
    title: ""
}

function AmazonIdeaGenerateTemplate({ fields }) {
    const [open, setOpen] = useState(false);
    const [state, setState] = useReducer(reducerFn, initialState);

    const [errors, setErrors] = useState({});

    const { setNotify, toggleToast } = useToastContext();

    const [setDelay] = useTimeout();
    const [createAmazonIdeaTemplate, { loading }] = useMutation(
        CREATE_AMAZON_IDEA_TEMPLATE,
        {
            onCompleted: () => {
                setNotify({
                    msg: "Create amazon idea template successfully",
                    err: false,
                });

                setDelay(() => {
                    handleClose();
                    setState(initialState)
                }, 1000);
            },
            onError: (err) => {
                setNotify({ msg: handleError(err?.toString()), err: true });
            },
        },
    );

    const handleOpen = useCallback(() => {
        setOpen(true);
    }, []);

    const handleClose = useCallback(() => {
        setOpen(false);
        setState(initialState)
    }, []);

    const handleStateChange = useCallback((value, id) => {
        setState({ [id]: value });

        const error = validateState(value, id);
        setErrors((p) => ({ ...p, ...error }));
    }, []);

    const handleValidateStates = async () => {
        let errors = {};
        Object.entries(state).forEach(([key, value]) => {
            const err = validateState(value, key);
            if (err) {
                errors = { ...errors, ...err };
            }
        });

        if (Object.values(errors).filter(Boolean).length > 0) {
            setErrors((p) => ({ ...p, ...errors }));
            return true;
        }

        return false;
    };

    const handleSubmit = useCallback(async () => {
        const hasError = await handleValidateStates();
        if (hasError) return;

        const input = prepareInput({ title: state.title, fields });

        toggleToast && toggleToast(true);
        setNotify && setNotify({ msg: null, err: false });

        createAmazonIdeaTemplate({
            variables: {
                input,
            },
        });
    }, [
        fields,
        state,
        handleValidateStates,
        createAmazonIdeaTemplate,
        toggleToast,
        setNotify,
    ]);

    return (
        <>
            <Button children="Generate a template" onClick={handleOpen} />
            <Modal
                title="Generate a template"
                sectioned
                open={open}
                onClose={handleClose}
                primaryAction={{
                    content: "Submit",
                    onAction: handleSubmit,
                    loading: loading,
                }}
                secondaryActions={[
                    { content: "Cancel", onAction: handleClose },
                ]}
            >
                <div>
                    <ComponentLabelPolaris label="Template Title" required />
                    <TextField
                        value={state.title}
                        id="title"
                        onChange={handleStateChange}
                        placeholder="Enter Template title"
                        error={errors["title"]}
                    />
                </div>
            </Modal>
        </>
    );
}

const FIELDS_REQUIRED = {
    title: "Idea Template title is required",
};

function validateState(value, id) {
    const res = {};

    if (FIELDS_REQUIRED[id]) {
        res[id] = null;

        if (!value) {
            res[id] = FIELDS_REQUIRED[id];
        } else if (typeof value !== "string" && arrInvalid(value)) {
            res[id] = FIELDS_REQUIRED[id];
        }
    }

    return res;
}

function prepareInput(state) {
    if (!arrInvalid(state.fields)) {
        state.fields = state.fields.map((field, index) => ({
            type: field.type,
            label: field.label,
            instructions: field.instructions,
            sampleText: field.sampleText,
            min: field.min ? Number(field.min) : undefined,
            max: field.max ? Number(field.max) : undefined,
            placeholder: field.placeholder,
            ordering: index,
            optionsList: (field.optionsList || []).map((opt) => ({
                name: opt.name,
                priceDifference: Number(opt?.priceDifference || 0),
            })),
        }));
    }

    const { kpi, ...input } = state;
    return input;
}

export default AmazonIdeaGenerateTemplate;
